import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Loader2, Send } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { OFFERINGS } from "./Offerings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = [...OFFERINGS.map((o) => o.title), "Partnering or funding", "Something else"];
const EMPTY = { name: "", email: "", interest: "", message: "" };

export const Enquiry = ({ headingAs: Heading = "h2" }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.interest) return toast.error("Please choose what you're interested in.");
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you. Your enquiry has reached us.");
      setForm(EMPTY);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setSending(false);
    }
  };

  const field = "h-12 rounded-sm border-border bg-background focus-visible:ring-primary";
  return (
    <section id="enquire" className="py-28 lg:py-36" data-testid="enquiry-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Enquire</p>
          <Heading className="mt-6 font-display text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl">
            Begin your journey <span className="text-primary">with us.</span>
          </Heading>
          <p className="mt-6 max-w-md text-base leading-relaxed text-foreground/75 sm:text-lg">
            Tell us a little about yourself and what draws you to Nia. Whether you want to learn, heal, join a retreat,
            contribute or partner with us, we will get back to you.
          </p>
          <a href="https://www.instagram.com/nia.ecoversity/" target="_blank" rel="noopener noreferrer" data-testid="enquiry-instagram-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-plum transition-colors hover:text-primary">
            <Instagram className="h-5 w-5" /> Follow along on Instagram @nia.ecoversity
          </a>
          <img src="/brand/nia-icon.png" alt="" aria-hidden="true" className="mt-12 h-40 w-auto opacity-90" />
        </div>
        <form onSubmit={submit} className="space-y-6 rounded-sm border border-border bg-card p-8 sm:p-12 lg:col-span-7" data-testid="enquiry-form">
          {done && (
            <div className="rounded-sm bg-leaf/10 px-5 py-4 text-sm font-medium text-leaf" data-testid="enquiry-success-message">
              Thank you. Your enquiry has reached us — we'll be in touch.
            </div>
          )}
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className={field} data-testid="enquiry-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className={field} data-testid="enquiry-email-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>I'm interested in</Label>
            <Select value={form.interest} onValueChange={set("interest")}>
              <SelectTrigger className={field} data-testid="enquiry-interest-select">
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                {INTERESTS.map((i, idx) => (
                  <SelectItem key={i} value={i} data-testid={`enquiry-interest-option-${idx}`}>{i}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">Message (optional)</Label>
            <Textarea id="message" rows={5} value={form.message} onChange={set("message")} className="rounded-sm border-border bg-background" data-testid="enquiry-message-input" />
          </div>
          <button type="submit" disabled={sending} data-testid="enquiry-submit-button"
            className="inline-flex w-full items-center justify-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send enquiry
          </button>
        </form>
      </div>
    </section>
  );
};
